import React, { FC, useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ReleaseNoteForm, {
    ReleaseNoteFormValues,
    SUPPORTED_LANGUAGES,
    TranslationContentMap,
} from 'components/release-notes/release-note-form/release-note-form.component';
import { updateReleaseNoteTemplate, getReleaseNotesTemplate } from 'apollo/templates/release-notes';
import { ReleaseNote, UpdateReleaseNoteInput } from 'models/release-note';

type Props = {
    open: boolean;
    onClose: () => void;
    releaseNoteData: ReleaseNote;
};

const validateTranslations = (translations: TranslationContentMap): string | undefined => {
    const hasEmptyTranslation = SUPPORTED_LANGUAGES.some(
        (language) => !(translations[language.code] || '').replace(/<[^>]*>/g, '').trim(),
    );
    if (hasEmptyTranslation) return 'ALL_TRANSLATIONS_REQUIRED';
    return undefined;
};

const buildInitialTranslations = (releaseNoteData: ReleaseNote): TranslationContentMap => {
    const translationMap = SUPPORTED_LANGUAGES.reduce<TranslationContentMap>((accumulator, language) => {
        accumulator[language.code] = '';
        return accumulator;
    }, {});
    releaseNoteData.translations.forEach((translation) => {
        translationMap[translation.language] = translation.content;
    });
    return translationMap;
};

const EditReleaseNoteDialog: FC<Props> = ({ open, onClose, releaseNoteData }) => {
    const { t: translate } = useTranslation(['release-notes', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [isTranslating, setIsTranslating] = useState(false);

    const [updateReleaseNote] = useMutation(updateReleaseNoteTemplate, {
        onError: (error) => showBoundary(error),
        refetchQueries: [{ query: getReleaseNotesTemplate }],
    });

    const formik = useFormik<ReleaseNoteFormValues>({
        initialValues: {
            organizationType: releaseNoteData.organizationType,
            translations: buildInitialTranslations(releaseNoteData),
            isPublished: releaseNoteData.isPublished,
        },
        enableReinitialize: true,
        validate: (values) => {
            const errors: Partial<Record<keyof ReleaseNoteFormValues, string>> = {};
            const contentError = validateTranslations(values.translations);
            if (contentError) errors.translations = translate(`release-notes:${contentError}`) as unknown as string;
            return errors;
        },
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: async (formValues) => {
            const releaseNote: UpdateReleaseNoteInput = {
                organizationType: formValues.organizationType,
                translations: SUPPORTED_LANGUAGES.filter((language) =>
                    formValues.translations[language.code]?.trim(),
                ).map((language) => ({
                    language: language.code,
                    content: formValues.translations[language.code],
                })),
                isPublished: formValues.isPublished,
            };

            await updateReleaseNote({ variables: { id: releaseNoteData._id, releaseNote } });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('release-notes:UPDATE_SUCCESS'),
            });
            onClose();
        },
    });

    useEffect(() => {
        if (!open) formik.resetForm();
    }, [open]);

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            title={translate('release-notes:EDIT_RELEASE_NOTE')}
            maxWidth="md"
            fullWidth
            disableBackdropClick
            actions={
                <Box display="flex" gap={1.5}>
                    <Button variant="text" onClick={handleClose} disabled={formik.isSubmitting}>
                        {translate('common:CANCEL')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting || isTranslating}
                        startIcon={formik.isSubmitting ? <CircularProgress size={16} /> : undefined}
                    >
                        {translate('release-notes:SAVE_CHANGES')}
                    </Button>
                </Box>
            }
        >
            <Box pt={1}>
                <ReleaseNoteForm
                    formik={formik}
                    versionDisplay={releaseNoteData.version}
                    onTranslatingChange={setIsTranslating}
                />
            </Box>
        </DHDialog>
    );
};

export default EditReleaseNoteDialog;
